import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader, StatusPill } from "@/components/common";
import { KidAvatar } from "@/components/Avatars";
import { Confirm, KIDS_RGB } from "@/components/kids/kidsUi";
import Approvals from "@/components/kids/Approvals";
import ChoreForm from "@/components/kids/ChoreForm";
import KidChores from "@/components/kids/KidChores";
import { useLiveRefresh } from "@/lib/live";
import { VoiceButton } from "@/components/voice/VoiceButton";

const STATUS = {
  todo: { label: "To do", rgb: "var(--glow-calendar)" },
  waiting: { label: "Waiting", rgb: "var(--glow-home)" },
  approved: { label: "Approved", rgb: "var(--glow-money)" },
  missed: { label: "Missed", rgb: "var(--glow-media)" },
};
const HISTORY = [{ id: "approved", label: "Approved" }, { id: "rejected", label: "Rejected" }, { id: "missed", label: "Missed" }];

function KidGroup({ kid, chores, instances, canManage, onEdit, onDelete }) {
  return (
    <section className="q-card" data-testid={`chores-kid-${kid.name.toLowerCase()}`}>
      <div className="mb-4 flex items-center gap-2.5"><KidAvatar avatar={kid.avatar} size={32} /><h3 className="font-display text-lg font-extrabold text-q-text">{kid.name}</h3></div>
      {chores.length === 0 ? <p className="text-sm text-q-muted">No chores yet.</p> : (
        <ul className="divide-y divide-q-border">
          {chores.map((c) => {
            const inst = instances.find((i) => i.chore_id === c.id);
            return (
              <li key={c.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`chore-row-${c.id}`}>
                <span className="text-2xl">{c.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-q-text">{c.title}</p>
                  <p className="text-xs text-q-muted">{c.schedule_label} · <span data-testid={`chore-reward-${c.id}`}>{money(c.reward)}</span></p>
                  {inst?.reject_note && inst.status === "todo" && <p className="text-xs text-q-accent">Try again: {inst.reject_note}</p>}
                </div>
                {inst && <StatusPill label={STATUS[inst.status]?.label || inst.status} rgb={STATUS[inst.status]?.rgb} testid={`chore-status-${c.id}`} />}
                {canManage && (
                  <div className="flex gap-1">
                    <button className="q-btn q-btn-ghost px-2 py-1.5" onClick={() => onEdit(c)} aria-label="Edit chore" data-testid={`chore-edit-btn-${c.id}`}><Pencil className="h-4 w-4" /></button>
                    <button className="q-btn q-btn-ghost px-2 py-1.5 hover:text-red-300" onClick={() => onDelete(c)} aria-label="Delete chore" data-testid={`chore-delete-btn-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function History({ hubId, canManage, onChanged }) {
  const [tab, setTab] = useState("approved");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hubId}/chores`, { params: { history: tab } }).then(({ data }) => setRows(data.history)), [hubId, tab]);
  useEffect(() => { setRows(null); load(); }, [load]);
  const undo = async (r) => {
    try {
      await api.post(`/organizations/${hubId}/chore-instances/${r.id}/undo-approval`);
      toast.success(`Took ${money(r.reward)} back`);
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mt-10" data-testid="chore-history">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h2 className="q-eyebrow mr-2">History</h2>
        {HISTORY.map((h) => <button key={h.id} className={`q-btn px-3 py-1.5 text-xs ${tab === h.id ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setTab(h.id)} data-testid={`chore-history-${h.id}`}>{h.label}</button>)}
      </div>
      {!rows ? <Loader /> : rows.length === 0 ? <p className="text-sm text-q-muted" data-testid="chore-history-empty">Nothing here yet.</p> : (
        <ul className="q-card divide-y divide-q-border p-0">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`history-row-${r.id}`}>
              <span className="text-xl">{r.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-q-text">{r.title} · {r.kid_name}</p>
                <p className="text-xs text-q-muted">{r.day === "once" ? "One time" : fmtDate(r.day)}{tab === "rejected" ? ` · ${r.reject_note}` : ""}</p>
              </div>
              <span className="text-sm font-bold text-q-text">{money(r.reward)}</span>
              {tab === "approved" && canManage && r.status === "approved" && (
                <button className="q-btn q-btn-ghost py-1.5 text-xs" onClick={() => undo(r)} data-testid={`undo-approval-btn-${r.id}`}><Undo2 className="h-3.5 w-3.5" /> Undo approval</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function Chores() {
  const { hub, reloadHub } = useHub();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [rev, setRev] = useState(0);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/chores`).then(({ data: d }) => setData(d)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), (m) => ["chore", "piggy.ledger"].includes(m.entity), null);
  useEffect(() => {
    if (data && window.location.hash === "#approvals") document.getElementById("approvals")?.scrollIntoView({ behavior: "smooth" });
  }, [data]);
  const changed = () => { load(); reloadHub(); setRev((n) => n + 1); };
  if (!data) return <Loader />;
  const isKid = data.is_kid;

  const remove = async () => {
    try {
      await api.delete(`/organizations/${hub.id}/chores/${del.id}`);
      toast.success("Chore deleted");
      setDel(null); changed();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="chores-page">
      <PageHeader eyebrow="Kids Corner" title={isKid ? "My chores" : "Chores"} rgb={KIDS_RGB} testid="chores-title"
        actions={<div className="flex gap-2"><VoiceButton hubId={hub.id} screen="chores" onSaved={changed} />{data.can_manage && <button className="q-btn q-btn-primary" onClick={() => setForm({})} data-testid="new-chore-btn"><Plus className="h-4 w-4" /> New chore</button>}</div>} />
      {isKid ? <KidChores data={data} hubId={hub.id} onChanged={changed} /> : (
        <>
          {!data.can_manage && <p className="mb-6 text-sm text-q-muted q-onwall" data-testid="chores-read-only">You can see everyone's chores. Only parents can change them.</p>}
          <Approvals items={data.approvals} hubId={hub.id} canManage={data.can_manage} onChanged={changed} />
          <h2 className="q-eyebrow mb-4">Chores by kid</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            {data.kids.map((k) => (
              <KidGroup key={k.id} kid={k} chores={data.chores.filter((c) => c.kid_id === k.id)} instances={data.instances.filter((i) => i.kid_id === k.id)}
                canManage={data.can_manage} onEdit={(c) => setForm({ chore: c })} onDelete={setDel} />
            ))}
          </div>
          <History key={rev} hubId={hub.id} canManage={data.can_manage} onChanged={changed} />
        </>
      )}
      {form && <ChoreForm hubId={hub.id} kids={data.kids} chore={form.chore} onClose={() => setForm(null)} onSaved={() => { setForm(null); changed(); }} />}
      <Confirm open={!!del} onClose={() => setDel(null)} title="Delete this chore? Money already earned stays." body={del ? `${del.icon} ${del.title}` : ""} onConfirm={remove} testid="chore-delete-confirm" />
    </div>
  );
}
